<template>
  <div>
    <v-form ref="form" v-model="valid">
      <teacher-lookup :showAddTeacherLink="true" v-model="selectedTeacher" :required="true" />
      <v-select
        v-model="selectedCenter"
        :items="availableCenters"
        label="Location"
        item-text="name"
        item-value="id"
        clearable
        :rules="[rules.required]"
      />
      <date-picker
        label="Start Date"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="selectedDate"
        :outlined="false"
        clearable
      />
      <v-text-field
        v-model="internalStartTime"
        label="Start Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-text-field
        v-model="internalEndTime"
        label="End Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-autocomplete
          v-if="selectedCenter"
          :items="centerClassrooms"
          v-model="selectedClassroom"
          item-value="id"
          item-text="roomNumber"
          label="Assigned Classroom"
          dense
          clearable
        />
      <v-textarea
        v-model="remark"
        label="Notes"
        auto-grow
        :rows="1"
        clearable
      />
      <v-checkbox v-model="isRecurring" label="Recurring Schedule?" />
      <div v-if="isRecurring">
        <date-picker
          label="End Date"
          :defaultNowOnNull="false"
          :show-hint="false"
          :show-icon="false"
          v-model="endDate"
          clearable
        />
        <div class="d-flex justify-space-around">
          <v-checkbox
            v-for="(day, index) in daysOfWeek"
            :key="index"
            v-model="day.selected"
            :label="day.label"
          />
        </div>
      </div>
      <v-card-actions>
        <v-btn
          class="mx-auto"
          color="green darken-1"
          text
          :loading="isLoading"
          @click="createSchedule"
        >
          Create Schedule
        </v-btn>

        <v-btn color="blue darken-1" text @click="onClose"> Close </v-btn>
      </v-card-actions>
    </v-form>

  </div>
</template>

<script>
import dialogMixin from './dialogMixin.js'
import { mapActions, mapGetters } from "vuex";
const TeacherLookup = () => import("@/components/teacher/TeacherLookup.vue");
import { EventBus } from "@/EventBus.js";
import { openOverlapScheduleErrors } from '@/components/schedule/eventHandler.js'

export default {
    mixins: [dialogMixin],
  props: {
    selectedTeacher: {
      type: Object,
      required: true,
    },
    selectedClassroom: {
      type: Object,
      required: true,
    },
    selectedDate: {
      type: Date,
      required: true,
    },
    startTime: {
      type: String,
      default: null,
    },
    endTime: {
      type: String,
      default: null,
    },
    classrooms: {
      type: Array,
      required: false,
    },
  },
  components: { TeacherLookup },
  computed: {
    ...mapGetters(["globalCenter", "myCenters"]),
    centerClassrooms() {
      if (this.selectedCenter && this.classrooms) {
        return this.classrooms.filter(
          (c) => c.centerId == this.selectedCenter
        );
      } else {
        return this.classrooms;
      }
    },
    availableCenters(){
      if(this.myCenters){
        return this.myCenters.filter(c => c.id);
      }
      else{ return [] }
    }
  },
  watch: {
    selectedTeacher(newVal) {
      this.selectedTeacher = newVal;
    },
    selectedDate(newVal) {
      this.selectedDate = newVal;
    },
    startTime(newVal) {
      this.internalStartTime = newVal;
    },
    endTime(newVal) {
      this.internalEndTime = newVal;
    },
  },
  data: () => ({
    isLoading: false,
    selectedTeacher: null,
    selectedCenter: null,
    isRecurring: false,
    endDate: null,
    internalStartTime: null,
    internalEndTime: null,
    remark: "",
    rules: {
      required: (value) => !!value || "Required.",
      endDate: (value) => {
        if (this.selectedDate && value) {
          return (
            new Date(value) >= new Date(this.selectedDate) ||
            "End date cannot be earlier than start date."
          );
        }
        return true;
      },
    },
    daysOfWeek: [
      { label: "Sun", selected: false },
      { label: "Mon", selected: false },
      { label: "Tue", selected: false },
      { label: "Wed", selected: false },
      { label: "Thu", selected: false },
      { label: "Fri", selected: false },
      { label: "Sat", selected: false },
    ],
  }),
  mounted() {
    this.selectedCenter = this.globalCenter.id;
    this.internalStartTime = this.startTime;
    this.internalEndTime = this.endTime;
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    validTimeRange() {
      if (this.internalStartTime && this.internalEndTime) {
        return (
          this.internalEndTime > this.internalStartTime ||
          "End time cannot be earlier than start time."
        );
      }
      return true;
    },
    validateDaysOfWeek() {
      if (this.isRecurring) {
        const atLeastOneDaySelected = this.daysOfWeek.some(
          (day) => day.selected
        );
        if (!atLeastOneDaySelected) {
          return "At least one day of the week must be selected for recurring schedules.";
        }
      }
      return true;
    },
    createSchedule() {
      if (!this.$refs.form.validate()) {
        return;
      }
      // Custom validation for days of the week
      const daysOfWeekValid = this.validateDaysOfWeek();
      if (daysOfWeekValid !== true) {
        this.addErrorMessage(daysOfWeekValid);
        return;
      }
      
      this.isLoading = true;

      const data = {
        centerId: this.selectedCenter,
        teacherId: this.selectedTeacher ? this.selectedTeacher.id : null,
        classroomId: this.selectedClassroom,
        startTime: this.internalStartTime,
        endTime: this.internalEndTime,
        recurring: this.isRecurring,
        startDate: this.selectedDate,
        endDate: this.endDate,
        notes: this.notes,
        daysOfWeek: this.daysOfWeek
          .filter((dow) => dow.selected)
          .map((dow) => dow.label),
        timezone: new Date().getTimezoneOffset(),
      };
      this.$axios
        .post("/teacher-schedule", data)
        .then((resp) => {
          this.isLoading = false;
          const savedSchedule = resp.data.data;
          this.addSuccessMessage("Teacher schedule added");
          this.$emit('on-close')
          EventBus.$emit('reload-chart', {savedSchedule})
        })
        .catch((error) => {
          this.isLoading = false;
          this.$emit("on-error", error);

          if(error.data && error.data.code == "OVERLAPPING_SCHEDULES"){
            const overlapSchedules = error.data.args[0];
            // Open a new dialog with overlap details
            openOverlapScheduleErrors({schedule: data, teacher: this.selectedTeacher, overlappingSchedules: overlapSchedules});
          }

          this.addErrorMessage(
            error.data.message || "Error creating new teacher schedule"
          );
        });
    },
  },
};
</script>
